/* eslint-disable no-console */

import React, { useState, useEffect } from 'react';
import PageStyle from './style';
import { Input, Select, TableProps, message, Tooltip } from 'antd';
import { Button, Image, Pagination, Table } from 'src/components';
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { SpinStyle } from 'src/components/style';

import DeleteModal from './delete-modal/DeleteModal';
import FormModal from './form-modal/FormModal';

import { useAppDispatch, useAppSelector } from 'src/stores';
import {
  deletePageAction,
  listPageAction,
} from 'src/stores/screens/page/page.action';
import { getCategoryAction } from 'src/stores/screens/category/category.action';
import { LANGUAGE, PAGE_LIMIT } from 'src/constants';
import { capitalizeFirstLetter } from 'src/utils';
import { FLAG_EN, FLAG_JA, FLAG_KO, FLAG_VI } from 'src/assets/images';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from 'src/components/breadcrumb';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const PageScreen: React.FC = () => {
  const navigator = useNavigate()
  const dispatch = useAppDispatch();
  const { pages, isLoading } = useAppSelector((state) => state.page);
  const { categories } = useAppSelector((state) => state.category);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [langue, setLangue] = useState(LANGUAGE.EN);
  const [currentRecord, setCurrentRecord] = useState<Page.PageType | null>(null);
  const [filter, setFilter] = useState({
    keyword: '',
    status: '',
    categoryId: '',
  });
  const [currentPage, setCurrentPage] = useState(1);

  const onFilterLangue = (value: string) => {
    setLangue(value);
  };

  const langues = [
    { label: <StyledWrapper> <Image src={FLAG_EN} /> EN </StyledWrapper>, value: LANGUAGE.EN },
    { label: <StyledWrapper> <Image src={FLAG_VI} /> VI </StyledWrapper>, value: LANGUAGE.VI },
    { label: <StyledWrapper> <Image src={FLAG_JA} /> JA </StyledWrapper>, value: LANGUAGE.JA },
    { label: <StyledWrapper> <Image src={FLAG_KO} /> KO </StyledWrapper>, value: LANGUAGE.KO },
  ]

  const columns: TableProps<any>['columns'] = [
    {
      title: '#',
      dataIndex: '#',
      key: '#',
      width: '80px',
      align: 'center',
      render: (id: string, _, index: number) => {
        const serialNumber = (currentPage - 1) * PAGE_LIMIT + (index + 1);
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '60px',
              whiteSpace: 'nowrap',
            }}
          >
            {serialNumber}
          </span>
        );
      },
    },
    {
      title: (
        <div className='langue-select'>
          <div>Name</div>
          <div>
            <Select
              style={{ width: 120 }}
              placeholder="langue"
              maxTagCount="responsive"
              options={langues}
              value={langue}
              onChange={(value) => onFilterLangue(value)}
            />
          </div>
        </div>
      ),
      dataIndex: `pageName${capitalizeFirstLetter(langue)}`,
      key: `pageName${capitalizeFirstLetter(langue)}`,
      className: 'min-w-200',
      render: (titleEn: string) => {
        return <p>{titleEn}</p>
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '180px',
      className: 'category',
      render: (category) => {
        const categoryName = category[`name${capitalizeFirstLetter(langue)}`]
        return categoryName;
      },
    },
    {
      title: 'Path',
      dataIndex: 'slug',
      key: 'slug',
      width: '200px',
      render: (value) => <p className='path'>{value}</p>
    },
    {
      title: 'Project link',
      dataIndex: 'url',
      key: 'url',
      width: '200px',
      render: (value) => value && (
        <Tooltip placement="top" title={value}>
          <p className='link' onClick={() => window.open(value, '_blank')}>{value}</p>
        </Tooltip>
      )
    },
    {
      title: 'Media',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      width: '90px',
      align: 'center',
      render: (fileUrl, { fileType }) => {
        const isVideo = fileType?.includes('video')
        if (isVideo) return 'VIDEO'
        return fileUrl && !isVideo && <img src={fileUrl} style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
      }
    },
    {
      title: 'Show',
      dataIndex: 'isShow',
      key: 'isShow',
      width: '90px',
      align: 'center',
      render: (isShow) => isShow ? <CheckOutlined /> : ''
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      width: 130,
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (_, record) => {
        return (
          <div className="action-box">
            <Tooltip placement="top" title={'Setting session'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator(`/page/${record.id}`)
                }}
              >
                <SettingOutlined />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={'Edit page'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(record);
                }}
              >
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={'Delete page'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentRecord(record);
                  setIsModalDeleteOpen(true);
                }}
              >
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    getCategory();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter.status, filter.categoryId]);

  const fetchData = (page = 1) => {
    const params: Page.GetListPageParams = cleanFilter({
      ...filter,
      page,
      status: filter.status,
    });
    void dispatch(listPageAction({ data: { ...params, limit: PAGE_LIMIT } }));
    setCurrentPage(page);
  };

  const getCategory = () => {
    void dispatch(getCategoryAction({ data: { limit: 20 } }));
  };

  const cleanFilter = <T extends Record<string, any>>(obj: T): T => {
    const cleanedObj: Partial<T> = {};

    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== '') {
        cleanedObj[key as keyof T] = obj[key];
      }
    });

    return cleanedObj as T;
  };

  const onCreate = () => {
    setCurrentRecord(null);
    setIsModalFormOpen(true);
  };

  const onEdit = (value: Page.PageType) => {
    setUpUser(value);
    setIsModalFormOpen(true);
  };

  const handleDeleteOk = async () => {
    if (currentRecord?.id) {
      const result: any = await dispatch(
        deletePageAction({ data: currentRecord.id })
      );
      setIsModalDeleteOpen(false);
      if (result?.error) {
        message.error('Delete failed');
      } else {
        message.success('Delete success');
        fetchData();
      }
    }
  };

  const setUpUser = (value: Page.PageType) => {
    setCurrentRecord(value);
  };

  const handleOkFormUser = () => {
    handleCancelModal();
  };

  const onChangePage = (value: number) => {
    fetchData(value);
  };

  const handleCancelModal = () => {
    setIsModalFormOpen(false);
  };

  const categoriesOptions = [
    { nameEn: 'All Categories', id: '' },
    ...categories?.records,
  ]?.map((item) => {
    return {
      label: item.nameEn,
      value: item.id,
    };
  });

  const onFilterCategory = (value: string) => {
    setFilter({ ...filter, categoryId: value });
  };

  return (
    <PageStyle className="container">
      <SpinStyle
        spinning={isLoading}
        wrapperClassName="root-spin"
        className="root-spin-component"
      >
        <div className="header-content">
          <Breadcrumb
            breadcrumbItems={[
              {
                name: 'Page',
                path: '/page',
              }
            ]}
          />
          <Button className="create-user" onClick={() => onCreate()}>
            Create
          </Button>
        </div>
        <div className="content">
          <div className="filter-box">
            <div className="total-box">
              Total: {pages?.total}
            </div>
            <div className="search-box">
              <Input
                placeholder="Search name..."
                prefix={<SearchOutlined />}
                onChange={(e) =>
                  setFilter({ ...filter, keyword: e.target.value.trim() })
                }
                onPressEnter={() => fetchData()}
              />
              <Button onClick={() => fetchData()}>Search</Button>
            </div>

            <Select
              style={{ width: 180 }}
              placeholder="Categories"
              maxTagCount="responsive"
              options={categoriesOptions}
              onChange={(value) => onFilterCategory(value)}
            />
          </div>
          <div className="data-table">
            <Table
              dataSource={pages.records || []}
              columns={columns}
              scroll={{ x: 'auto', y: pages.records.length ? window.innerHeight - 380 : undefined }}
            />
            <Pagination
              total={pages.total}
              onChange={onChangePage}
              current={currentPage}
              pageSize={pages.limit}
            />
          </div>
        </div>
        <DeleteModal
          isModalDeleteOpen={isModalDeleteOpen}
          handleCancel={() => setIsModalDeleteOpen(false)}
          handleOk={handleDeleteOk}
        />
        {isModalFormOpen && (
          <FormModal
            isModalFormOpen={isModalFormOpen}
            handleCancel={handleCancelModal}
            handleOk={handleOkFormUser}
            currentRecord={currentRecord}
            fetchData={fetchData}
            currentPage={currentPage}
          />
        )}
      </SpinStyle>
    </PageStyle>
  )
};

export default PageScreen;
